<template>
  <section class="ma-4 ma-md-8">
    <t-page-title title="Mapeamento de Locais" :on-add="() => $router.push({ name: 'create-mappings' })" />
    <v-row justify="center" align="center">
      <v-col>
        <data-table
          ref="datatable"
          :headers="headers"
          :provider="fetch"
          :filters="filters"
          :actions="actions"
        >
          <template #[`item.created_at`]="{ item }">
            <span> {{ $luxon.DateTime.fromFormat(item.created_at, 'yyyy-MM-dd HH:mm:ss').toFormat('dd/MM/yyyy HH:mm:ss') }}</span>
          </template>
          <template #[`item.id`]="{ item }">
            <span> {{ item.id.split('-')[0] }}</span>
          </template>
          <template #[`item.active`]="{ item }">
            <span> {{ item.active ? 'Ativo' : 'Inativo' }}</span>
          </template>
        </data-table>
      </v-col>
    </v-row>
  </section>
</template>

<script lang="ts" setup>
import { DataTableFilter, DataTableHeader, DefaultDataTableActions } from '@/types/DataTable'
import DataTable from '@/components/datatable/DataTable.vue'
import TPageTitle from '@/components/TPageTitle.vue'
import MappingService from '@/services/MappingService'
import Mapping from '@/types/Mapping'
import { useSnackBar } from '@/stores/snackbar'
import { useDialog } from '@/stores/dialog'
import { useLoader } from '@/stores/loader'
import { ref, useTemplateRef } from 'vue'
import { useRouter } from 'vue-router'
import { useAbility } from '@casl/vue'
import { useTextFilter } from '@/helpers/reports/textFilter'
import { useSelectFilter } from '@/helpers/reports/selectFilter'

const { can } = useAbility()
const dialog = useDialog()
const loader = useLoader()
const router = useRouter()
const snackbar = useSnackBar()

const datatable = useTemplateRef('datatable')

const headers: DataTableHeader[] = [
  { title: 'ID', value: 'id', key: 'id', sortable: false },
  { title: 'Nome', value: 'name', key: 'name', sortable: false },
  { title: 'Criado em', value: 'created_at', key: 'created_at', sortable: false },
  { title: 'Status', value: 'active', key: 'active', sortable: false },
  { title: 'Ações', value: 'actions', key: 'actions', sortable: false, align: 'center' },
]

const actions: DefaultDataTableActions<Mapping> = {
  update: {
    callback: item => router.push({ name: 'edit-mappings', params: { id: item.id } }),
    condition: () => can('update', 'mapping-locations'),
    color: 'info',
  },
  destroy: {
    callback: item => destroy(item),
    condition: () => can('delete', 'mapping-locations'),
    color: 'error',
  },
}

const filters = ref<DataTableFilter[]>([
  useTextFilter({
    field: 'name',
    label: 'Nome',
    icon: 'fa-regular fa-signature',
    required: false,
  }),
  useSelectFilter({
    field: 'status',
    label: 'Status',
    icon: 'fa-regular fa-circle',
    items: [
      { title: 'Todos', value: '' },
      { title: 'Ativo', value: '1' },
      { title: 'Inativo', value: '0' },
    ],
    initial: '1',
    required: false,
  }),
])

async function fetch(page: number, filters: string, per_page?: number) {
  try {
    return await (new MappingService()).index(page, filters, per_page)
  } catch (error) {
    snackbar.show(
      'Não foi possível buscar os Locais',
      'error',
    )

    throw error
  }
}

async function destroy(item: Mapping) {
  const confirm = await dialog.open({
    title: 'Confirmação de exclusão',
    message: 'Realmente deseja excluir o mapeamento? Essa ação é irreversível',
  })

  if (!confirm) return

  try {
    loader.increment()
    await (new MappingService()).destroy(item.id)

    snackbar.show(
      'Mapeamento excluído com sucesso.',
      'success',
    )

    datatable.value?.fetch(1)
  } catch (error) {
    snackbar.show(
      'Não foi possível atualizar o mapeamento',
      'error',
    )

    throw error
  } finally {
    loader.decrement()
  }
}

</script>
